import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { UserRole } from '../../models/User';
import { User } from '../../models/User';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: {
    email: string;
    name: string;
    role: UserRole;
    supervisorId?: string;
    credentials?: string;
    specializations?: string[];
  }) => Promise<void>;
  supervisors: User[];
}

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  supervisors,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'rbt' as UserRole,
    supervisorId: '',
    credentials: '',
    specializations: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        email: '',
        name: '',
        role: 'rbt',
        supervisorId: '',
        credentials: '',
        specializations: '',
      });
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Parse specializations from comma-separated string
      const specializationsArray = formData.specializations
        ? formData.specializations.split(',').map((s) => s.trim()).filter((s) => s)
        : undefined;

      await onSubmit({
        email: formData.email,
        name: formData.name,
        role: formData.role,
        supervisorId: (formData.role === 'rbt' || formData.role === 'supervisor') && formData.supervisorId ? formData.supervisorId : undefined,
        credentials: formData.credentials || undefined,
        specializations: specializationsArray,
      });

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: 'var(--spacing-4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
            Add New User
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-gray-400)',
              padding: 'var(--spacing-2)',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-4)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email *
            </label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="user@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="name">
              Full Name *
            </label>
            <input
              id="name"
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="role">
              Role *
            </label>
            <select
              id="role"
              className="form-input"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              required
            >
              <option value="rbt">RBT</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
              <option value="hr">HR Manager</option>
              <option value="viewer">Viewer</option>
            </select>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginTop: 'var(--spacing-2)' }}>
              {formData.role === 'rbt' && 'Can upload and view sessions, needs supervisor assignment'}
              {formData.role === 'supervisor' && 'Can review sessions and manage RBTs'}
              {formData.role === 'admin' && 'Full access to all features'}
              {formData.role === 'hr' && 'Can manage users, payroll, and timesheets'}
              {formData.role === 'viewer' && 'Read-only access to footage'}
            </p>
          </div>

          {(formData.role === 'rbt' || formData.role === 'supervisor') && (
            <div className="form-group">
              <label className="form-label" htmlFor="supervisor">
                {formData.role === 'rbt' ? 'Assign Supervisor' : 'Assign Supervising Supervisor'}
              </label>
              <select
                id="supervisor"
                className="form-input"
                value={formData.supervisorId}
                onChange={(e) => setFormData({ ...formData, supervisorId: e.target.value })}
              >
                <option value="">Unassigned (can be assigned later)</option>
                {supervisors.map((supervisor) => (
                  <option key={supervisor.id} value={supervisor.id}>
                    {supervisor.name} ({supervisor.email})
                  </option>
                ))}
              </select>
              {supervisors.length === 0 && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-warning)', marginTop: 'var(--spacing-2)' }}>
                  No supervisors available. Create a supervisor user first.
                </p>
              )}
              {formData.role === 'supervisor' && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginTop: 'var(--spacing-2)' }}>
                  Supervisors can also be assigned to other supervisors for oversight
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="credentials">
              Credentials (Optional)
            </label>
            <input
              id="credentials"
              type="text"
              className="form-input"
              value={formData.credentials}
              onChange={(e) => setFormData({ ...formData, credentials: e.target.value })}
              placeholder="e.g., BCBA, RBT, OT, SLP"
            />
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginTop: 'var(--spacing-2)' }}>
              Professional certifications or credentials
            </p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="specializations">
              Specializations (Optional)
            </label>
            <input
              id="specializations"
              type="text"
              className="form-input"
              value={formData.specializations}
              onChange={(e) => setFormData({ ...formData, specializations: e.target.value })}
              placeholder="e.g., ABA, Early Intervention, Autism"
            />
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginTop: 'var(--spacing-2)' }}>
              Comma-separated areas of expertise
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              marginTop: 'var(--spacing-6)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{ flex: 1 }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={loading}
            >
              {loading ? 'Creating User...' : 'Create User'}
            </button>
          </div>

          <div
            style={{
              marginTop: 'var(--spacing-6)',
              padding: 'var(--spacing-4)',
              backgroundColor: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
              <strong>Note:</strong> The user will need to click "Forgot Password" on the login page to set their own password. Check the browser console for the temporary password (for testing only).
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
